import { useIsMobile } from "Hooks/useIsMobile";
import { useCallback } from "react";
import {
  type NavigateOptions,
  type To,
  useLocation,
  // biome-ignore lint/style/noRestrictedImports: We're creating our wrapped useNavigate version here
  useNavigate as useRouterNavigate,
} from "react-router-dom";

const BASE_URL_PATHNAME = "/";
const HISTORY_STACK_WITH_BASE_URL = [
  {
    pathname: BASE_URL_PATHNAME,
    search: "",
    hash: "",
    state: { locationHistoryStack: [] },
    key: "unused",
  },
];

export type NavigateFunction = (
  to: To, // omit the relative navigation using number
  options?: NavigateOptions & { resetHistory?: boolean },
) => void;

export function useNavigate() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigate = useRouterNavigate();

  const customNavigate: NavigateFunction = useCallback(
    (to, options) => {
      const locationHistoryStack = options?.resetHistory
        ? isMobile
          ? HISTORY_STACK_WITH_BASE_URL
          : []
        : location.state?.locationHistoryStack || [];

      let parsedTo: To = to;
      if (typeof to === "object" && to?.pathname?.includes("?")) {
        const [pathname, searchPart] = to.pathname.split("?");
        const search = searchPart ? `?${searchPart}` : "";
        parsedTo = { pathname, search };
      }

      const wasReplaceRequested = options?.replace ?? false;
      const isCurrentLocationBaseUrl = location.pathname === BASE_URL_PATHNAME;
      const shouldReplace = wasReplaceRequested && !isCurrentLocationBaseUrl;

      navigate(parsedTo, {
        ...options,
        replace: shouldReplace,
        state: {
          ...options?.state,
          locationHistoryStack: shouldReplace
            ? locationHistoryStack
            : [location, ...locationHistoryStack],
        },
      });
    },
    [location, navigate, isMobile],
  );

  return customNavigate;
}
